import {
  Users,
  ShieldCheck,
  Zap,
  Globe,
  Clock,
  BarChart3,
} from "lucide-react";
import Button from "../components/Button";
import { type ReactNode } from "react";

interface FeatureItemProps {
  icon: ReactNode;
  title: string;
  description: string;
}

function FeatureCard({ icon, title, description }: FeatureItemProps) {
  return (
    <div className="rounded-xl bg-white/[0.03] border border-surface-border/30 p-6 hover:bg-white/[0.06] transition-all duration-300 group h-full">
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-accent-blue/10 flex items-center justify-center text-accent-blue group-hover:bg-accent-blue/20 transition-colors duration-300">
          {icon}
        </div>
        <div>
          <h4 className="text-base font-semibold text-white mb-1">{title}</h4>
          <p className="text-sm text-gray-400 leading-relaxed">{description}</p>
        </div>
      </div>
    </div>
  );
}

const features: FeatureItemProps[] = [
  {
    icon: <Users size={20} />,
    title: "Verified Worker Pool",
    description:
      "Access phone-verified chatters, VAs, and OFM workers. Every applicant is real and vetted.",
  },
  {
    icon: <ShieldCheck size={20} />,
    title: "Escrow-Protected Contracts",
    description:
      "Fund contracts with crypto escrow. Pay only when deliverables are met.",
  },
  {
    icon: <Zap size={20} />,
    title: "Instant Hiring Pipeline",
    description:
      "Post a job, receive applications, and hire within hours. No bureaucracy.",
  },
  {
    icon: <Globe size={20} />,
    title: "Global Talent, No Borders",
    description:
      "Hire from anywhere in the world. Crypto payments eliminate cross-border banking issues.",
  },
  {
    icon: <Clock size={20} />,
    title: "Time-Tracked Deliverables",
    description:
      "Monitor progress with built-in time tracking and milestone checkpoints.",
  },
  {
    icon: <BarChart3 size={20} />,
    title: "Performance Analytics",
    description:
      "Track worker ratings, completion rates, and contract history at a glance.",
  },
];

export default function ForAgencies() {
  return (
    <section
      id="for-agencies"
      className="relative bg-[#0A1628] noise-overlay"
    >
      {/* Subtle gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-accent-blue/5 via-transparent to-transparent pointer-events-none" />

      <div className="section-padding relative">
        <div className="section-container">
          {/* Header — left aligned with accent line */}
          <div className="mb-16">
            <span className="text-accent-blue uppercase tracking-widest text-xs font-mono mb-4 block">
              For Agencies
            </span>
            <div className="accent-line-blue max-w-3xl">
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 leading-[1.1]">
                Hire Verified Workers{" "}
                <span className="text-gradient">Instantly</span>
              </h2>
              <p className="text-lg text-gray-400 leading-relaxed max-w-2xl">
                Build your team with confidence. Every worker is phone-verified,
                every payment is escrowed, and every interaction stays
                anonymous until you decide otherwise.
              </p>
            </div>
          </div>

          {/* Staggered 12-column feature grid */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 mb-12">
            {/* Row 1: 7 + 5 */}
            <div className="lg:col-span-7">
              <FeatureCard {...features[0]!} />
            </div>
            <div className="lg:col-span-5">
              <FeatureCard {...features[1]!} />
            </div>

            {/* Row 2: 5 + 7 (offset) */}
            <div className="lg:col-span-5">
              <FeatureCard {...features[2]!} />
            </div>
            <div className="lg:col-span-7">
              <FeatureCard {...features[3]!} />
            </div>

            {/* Row 3: 6 + 6 */}
            <div className="lg:col-span-6">
              <FeatureCard {...features[4]!} />
            </div>
            <div className="lg:col-span-6">
              <FeatureCard {...features[5]!} />
            </div>
          </div>

          {/* CTA */}
          <Button
            variant="gradient"
            size="lg"
            href="#cta"
            className="!bg-gradient-blue"
          >
            Start Hiring
          </Button>
        </div>
      </div>
    </section>
  );
}
